import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getColorClasses } from '@/lib/design-system';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  buttonHover,
  buttonTap,
  focusRing,
  ANIMATION_TIMING,
} from '@/lib/animations';

interface AnimatedButtonProps {
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  delay?: number;
}

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  className,
  onClick,
  disabled = false,
  icon,
  delay = 0,
}) => {
  const prefersReducedMotion = useReducedMotion();

  const baseClasses = cn(
    'font-semibold transition-all focus:outline-none',
    'disabled:bg-brand-disabled disabled:text-brand-disabled-text disabled:cursor-not-allowed',
    getColorClasses.focus()
  );

  const variantClasses = {
    primary: cn(getColorClasses.background.primary(), 'text-white shadow-lg'),
    success: cn(getColorClasses.background.success(), 'text-white shadow-lg'),
    warning: cn(
      getColorClasses.background.warning(),
      getColorClasses.text.textPrimary(),
      'shadow-lg'
    ),
    danger: cn(getColorClasses.background.danger(), 'text-white shadow-lg'),
    outline: cn(
      'border-2',
      getColorClasses.border.primary(),
      getColorClasses.text.primary(),
      'bg-transparent'
    ),
    ghost: cn(getColorClasses.text.primary(), 'bg-transparent'),
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm rounded-md',
    md: 'px-6 py-3 text-base rounded-lg',
    lg: 'px-8 py-4 text-lg rounded-lg',
  };

  // Animation variants
  const buttonVariants = {
    initial: {
      opacity: 0,
      scale: 0.9,
      y: 20,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
    },
    hover: prefersReducedMotion
      ? {}
      : {
          ...buttonHover,
          boxShadow:
            variant === 'outline'
              ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
              : '0 10px 25px rgba(0, 0, 0, 0.2)',
        },
    tap: prefersReducedMotion ? {} : buttonTap,
    focus: prefersReducedMotion ? {} : focusRing,
  };

  const animateTransition = {
    duration: ANIMATION_TIMING.standard,
    ease: [0.68, -0.55, 0.265, 1.55] as const, // Bounce
    delay,
  };

  // Outline hover effect
  const outlineHoverClass =
    variant === 'outline' ? 'hover:bg-brand-primary hover:text-white' : '';

  const ghostHoverClass =
    variant === 'ghost' ? 'hover:bg-brand-primary/10' : '';

  const ButtonComponent = prefersReducedMotion ? Button : motion.button;

  const buttonProps = prefersReducedMotion
    ? {
        className: cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          outlineHoverClass,
          ghostHoverClass,
          className
        ),
        onClick,
        disabled,
      }
    : {
        className: cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          outlineHoverClass,
          ghostHoverClass,
          className
        ),
        variants: buttonVariants,
        initial: 'initial',
        animate: 'animate',
        transition: animateTransition,
        whileHover: disabled ? {} : 'hover',
        whileTap: disabled ? {} : 'tap',
        whileFocus: disabled ? {} : 'focus',
        onClick,
        disabled,
      };

  return (
    <ButtonComponent {...buttonProps}>
      <div className='flex items-center gap-2'>
        {icon && (
          <motion.span
            initial={prefersReducedMotion ? {} : { scale: 0 }}
            animate={prefersReducedMotion ? {} : { scale: 1 }}
            transition={{
              delay: delay + 0.1,
              duration: ANIMATION_TIMING.fast,
              ease: [0.68, -0.55, 0.265, 1.55],
            }}
          >
            {icon}
          </motion.span>
        )}
        {children}
      </div>
    </ButtonComponent>
  );
};
